import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UploadCloud, PenLine, CheckCircle2, Layers, Sparkles, Wand2 } from "lucide-react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { DEAL_TEMPLATES } from "../lib/dealTemplates";
import { CONTENT_TYPES, STAGES, REGIONS } from "../lib/filters";
import { toast } from "sonner";

const DEFAULT = {
    title: "",
    synopsis: "",
    genres: "",
    country: "India",
    language: "Hindi",
    cast: "",
    crew: "",
    runtime: 110,
    poster_url:
        "https://images.pexels.com/photos/7991579/pexels-photo-7991579.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    trailer_url: "",
    trailer_youtube_id: "HUECWi5pX7o",
    full_movie_url: "",
    price_usd: 50000,
    content_type: "Feature Film",
    region: "Global",
    stage: "Completed",
    rights_type: ["Full Copyright Ownership"],
    exclusivity: "Exclusive",
};

export default function CreatorStudio() {
    const [form, setForm] = useState(DEFAULT);
    const [film, setFilm] = useState(null);
    const [signatureName, setSignatureName] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [signed, setSigned] = useState(false);
    const [pitch, setPitch] = useState({ loading: false, data: null });
    const [posterGen, setPosterGen] = useState(false);
    const { user } = useAuth();

    const runPitchAnalyze = async () => {
        if (!form.title || !form.synopsis) return toast.error("Add title and synopsis first");
        setPitch({ loading: true, data: null });
        try {
            const { data } = await api.post("/ai/pitch-analyze", {
                title: form.title,
                synopsis: form.synopsis,
                genres: (form.genres || "").split(",").map(s => s.trim()).filter(Boolean),
                country: form.country, language: form.language,
            });
            setPitch({ loading: false, data });
        } catch (e) {
            setPitch({ loading: false, data: null });
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "AI analysis failed");
        }
    };

    const generatePoster = async () => {
        if (!form.title || !form.synopsis) return toast.error("Add title and synopsis first");
        setPosterGen(true);
        try {
            const { data } = await api.post("/ai/generate-poster", {
                title: form.title,
                synopsis: form.synopsis,
            });
            setForm(f => ({ ...f, poster_url: data.data_url }));
            toast.success("Poster generated");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Poster gen failed");
        } finally {
            setPosterGen(false);
        }
    };
    const nav = useNavigate();

    const set = (k) => (e) =>
        setForm({
            ...form,
            [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value,
        });

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const payload = {
                ...form,
                genres: form.genres.split(",").map((s) => s.trim()).filter(Boolean),
                cast: form.cast.split(",").map((s) => s.trim()).filter(Boolean),
                crew: form.crew.split(",").map((s) => s.trim()).filter(Boolean),
            };
            const { data } = await api.post("/films", payload);
            setFilm(data);
            setSignatureName(user?.name || "");
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    const sign = async () => {
        if (!signatureName.trim()) {
            alert("Type your legal name to sign.");
            return;
        }
        try {
            await api.post("/contracts/sign", {
                film_id: film.id,
                signature_name: signatureName.trim(),
                contract_type: "creator_assignment",
            });
            setSigned(true);
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-16">
                <div className="mb-10">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-3">
                        Upload Studio
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl leading-none">
                        Assign your <span className="silver-text italic">film.</span>
                    </h1>
                    <p className="text-silver-400 mt-4 max-w-xl">
                        Add the master, fill in the metadata and sign the assignment deed.
                        Admins review within 24 hours before listing.
                    </p>
                </div>

                {!film ? (
                    <form onSubmit={submit} className="grid lg:grid-cols-[1.3fr_1fr] gap-8">
                        <div className="glass p-8 rounded-sm space-y-5">
                            {/* AI quick tools */}
                            <div className="flex flex-wrap gap-2 pb-3 border-b border-white/5">
                                <button
                                    type="button"
                                    onClick={runPitchAnalyze}
                                    disabled={pitch.loading}
                                    className="inline-flex items-center gap-2 border border-platinum/40 text-platinum hover:bg-platinum/10 px-3 py-2 text-xs uppercase tracking-widest rounded-sm disabled:opacity-50"
                                    data-testid="ai-pitch-btn"
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    {pitch.loading ? "Analysing…" : "AI Pitch Analyze"}
                                </button>
                                <button
                                    type="button"
                                    onClick={generatePoster}
                                    disabled={posterGen}
                                    className="inline-flex items-center gap-2 border border-platinum/40 text-platinum hover:bg-platinum/10 px-3 py-2 text-xs uppercase tracking-widest rounded-sm disabled:opacity-50"
                                    data-testid="ai-poster-btn"
                                >
                                    <Wand2 className="w-3.5 h-3.5" />
                                    {posterGen ? "Generating…" : "Generate Poster (Nano Banana)"}
                                </button>
                            </div>

                            {pitch.data && (
                                <div className="bg-ink-200/60 border border-platinum/30 rounded-sm p-5" data-testid="ai-pitch-result">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400">Market-fit score</div>
                                        <div className="font-display text-3xl silver-text">{pitch.data.market_fit_score}<span className="text-silver-500 text-lg">/100</span></div>
                                    </div>
                                    <div className="text-xs tracking-widest uppercase text-silver-500 mt-3 mb-1">Auto logline</div>
                                    <div className="text-sm text-silver-200 italic">"{pitch.data.one_line_logline}"</div>
                                    <div className="text-xs tracking-widest uppercase text-silver-500 mt-4 mb-1">Buyer archetype</div>
                                    <div className="text-sm text-silver-300">{pitch.data.buyer_archetype}</div>
                                    <div className="text-xs tracking-widest uppercase text-silver-500 mt-4 mb-1">Tags</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {(pitch.data.genre_tags || []).map(t => (
                                            <span key={t} className="text-[11px] bg-platinum/10 border border-platinum/30 text-platinum px-2 py-0.5 rounded-sm">{t}</span>
                                        ))}
                                    </div>
                                    {pitch.data.comparable_titles?.length > 0 && (
                                        <>
                                            <div className="text-xs tracking-widest uppercase text-silver-500 mt-4 mb-1">Comparable titles</div>
                                            <ul className="text-xs text-silver-400 space-y-1">
                                                {pitch.data.comparable_titles.map((c, i) => (
                                                    <li key={i}><span className="text-silver-200">{c.title}</span> — {c.why}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Deal template picker */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Layers className="w-4 h-4 text-platinum" />
                                    <label className="text-[10px] uppercase tracking-widest text-silver-400">
                                        Quick-start: deal template
                                    </label>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {DEAL_TEMPLATES.map((t) => {
                                        const active = JSON.stringify(form.rights_type || []) === JSON.stringify(t.rights_type);
                                        return (
                                            <button
                                                type="button"
                                                key={t.key}
                                                onClick={() =>
                                                    setForm((f) => ({
                                                        ...f,
                                                        rights_type: t.rights_type,
                                                        exclusivity: t.exclusivity,
                                                    }))
                                                }
                                                className={`text-left p-3 rounded-sm border transition-colors ${
                                                    active
                                                        ? "border-platinum bg-platinum/10 text-white"
                                                        : "border-white/10 hover:border-platinum/40 text-silver-300"
                                                }`}
                                                data-testid={`template-${t.key}`}
                                            >
                                                <div className="text-xs font-medium mb-1">{t.name}</div>
                                                <div className="text-[11px] text-silver-500 leading-relaxed">
                                                    {t.description}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="mt-2 text-[11px] text-silver-400">
                                    Selected rights: <span className="text-platinum">{(form.rights_type || []).join(" · ")}</span> · {form.exclusivity}
                                </div>
                            </div>

                            <div className="h-px bg-white/5" />

                            <Row label="Title" value={form.title} onChange={set("title")} testid="studio-title" required />
                            <Row
                                label="Synopsis"
                                value={form.synopsis}
                                onChange={set("synopsis")}
                                as="textarea"
                                testid="studio-synopsis"
                                required
                            />
                            <div className="grid grid-cols-3 gap-4">
                                <Select
                                    label="Content Type"
                                    value={form.content_type}
                                    options={CONTENT_TYPES}
                                    onChange={(v) => setForm({ ...form, content_type: v })}
                                    testid="studio-content-type"
                                />
                                <Select
                                    label="Stage"
                                    value={form.stage}
                                    options={STAGES}
                                    onChange={(v) => setForm({ ...form, stage: v })}
                                    testid="studio-stage"
                                />
                                <Select
                                    label="Region"
                                    value={form.region}
                                    options={REGIONS}
                                    onChange={(v) => setForm({ ...form, region: v })}
                                    testid="studio-region"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <Row label="Country" value={form.country} onChange={set("country")} testid="studio-country" />
                                <Row label="Language" value={form.language} onChange={set("language")} testid="studio-language" />
                            </div>
                            <Row label="Genres (comma-separated)" value={form.genres} onChange={set("genres")} testid="studio-genres" />
                            <Row label="Cast (comma-separated)" value={form.cast} onChange={set("cast")} testid="studio-cast" />
                            <Row label="Crew (comma-separated)" value={form.crew} onChange={set("crew")} testid="studio-crew" />
                            <div className="grid grid-cols-2 gap-4">
                                <Row label="Runtime (min)" type="number" value={form.runtime} onChange={set("runtime")} testid="studio-runtime" />
                                <Row label="Asking price (USD)" type="number" value={form.price_usd} onChange={set("price_usd")} testid="studio-price" />
                            </div>
                            <Row label="Poster URL" value={form.poster_url} onChange={set("poster_url")} testid="studio-poster" />
                            <Row label="YouTube trailer ID" value={form.trailer_youtube_id} onChange={set("trailer_youtube_id")} testid="studio-trailer-yt" />
                            <Row label="Full Movie URL (secure)" value={form.full_movie_url} onChange={set("full_movie_url")} testid="studio-full" />
                            {err && <div className="text-sm text-red-400" data-testid="studio-error">{err}</div>}
                            <button
                                type="submit"
                                disabled={loading}
                                className="inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 disabled:opacity-50 text-ink-300 px-6 py-3 rounded-sm uppercase tracking-widest text-xs font-medium"
                                data-testid="studio-submit"
                            >
                                <UploadCloud className="w-4 h-4" />
                                {loading ? "Uploading…" : "Save Film"}
                            </button>
                        </div>

                        <div className="glass rounded-sm p-6">
                            <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400 mb-3">
                                Poster preview
                            </div>
                            <div className="aspect-[2/3] overflow-hidden rounded-sm border border-white/10 mb-5 bg-ink-200">
                                {form.poster_url && (
                                    <img src={form.poster_url} alt="preview" className="w-full h-full object-cover" />
                                )}
                            </div>
                            <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400 mb-3">
                                Trailer preview
                            </div>
                            {form.trailer_url ? (
                                <video
                                    src={form.trailer_url}
                                    controls
                                    className="w-full aspect-video rounded-sm border border-white/10 bg-black"
                                />
                            ) : (
                                <div className="w-full aspect-video rounded-sm border border-white/10 bg-black" />
                            )}
                        </div>
                    </form>
                ) : signed ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="glass rounded-sm p-12 text-center max-w-2xl mx-auto"
                        data-testid="studio-submitted"
                    >
                        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-6" />
                        <h2 className="font-display text-4xl mb-3">Submitted for review</h2>
                        <p className="text-silver-400 mb-8">
                            {film.title} has been assigned to Silver Screen Xchange. Our legal team will
                            verify chain-of-title and approve it for the exchange.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => {
                                    setFilm(null);
                                    setForm(DEFAULT);
                                    setSigned(false);
                                }}
                                className="text-sm px-5 py-2.5 border border-white/10 hover:border-platinum rounded-sm"
                                data-testid="studio-upload-another"
                            >
                                Upload another
                            </button>
                            <button
                                onClick={() => nav("/my-films")}
                                className="text-sm px-5 py-2.5 bg-platinum text-ink-300 rounded-sm"
                                data-testid="studio-view-my-films"
                            >
                                View my films
                            </button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="grid lg:grid-cols-[1.3fr_1fr] gap-8"
                    >
                        <div className="glass rounded-sm p-8">
                            <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-3">
                                Step 2 · Assignment Deed
                            </div>
                            <h2 className="font-display text-3xl mb-4">
                                Sign over {film.title}
                            </h2>
                            <p className="text-silver-400 text-sm mb-5">
                                By signing below you assign full copyright to Silver Screen Xchange
                                under the Indian Copyright Act, 1957 for onward exclusive
                                transfer to a buyer.
                            </p>
                            <div className="bg-ink-200 border border-white/5 rounded-sm p-5 text-[13px] leading-relaxed text-silver-200 max-h-[360px] overflow-y-auto" data-testid="creator-deed-text">
                                <pre className="whitespace-pre-wrap font-sans">
{`ASSIGNMENT DEED OF COPYRIGHT

I, ${signatureName || "[your name]"}, being the lawful author/producer of the cinematograph work "${film.title}" (${film.language}, ${film.country}), hereby assign the full copyright — including all exclusive rights under Section 14 of the Indian Copyright Act, 1957 — to Silver Screen Xchange Pvt. Ltd. for the purpose of onward exclusive transfer to a verified Buyer.

Warranties: sole ownership, no infringement, clean chain-of-title.
Indemnity: unlimited, in favour of Silver Screen Xchange and the eventual Buyer.
Jurisdiction: Courts of Mumbai, Maharashtra, India.
`}
                                </pre>
                            </div>

                            <div className="mt-5 flex flex-wrap items-end gap-5">
                                <div className="flex-1 min-w-[240px]">
                                    <label className="text-[10px] uppercase tracking-widest text-silver-400">
                                        Sign with legal name
                                    </label>
                                    <input
                                        value={signatureName}
                                        onChange={(e) => setSignatureName(e.target.value)}
                                        className="signature-pad w-full bg-transparent border-b border-platinum/40 focus:border-platinum focus:outline-none py-1"
                                        placeholder="Your signature"
                                        data-testid="creator-signature-input"
                                    />
                                </div>
                                <button
                                    onClick={sign}
                                    className="inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 text-ink-300 px-5 py-3 rounded-sm uppercase tracking-widest text-xs font-medium"
                                    data-testid="creator-sign-deed-btn"
                                >
                                    <PenLine className="w-4 h-4" /> Sign & Submit
                                </button>
                            </div>
                        </div>

                        <div className="glass rounded-sm p-6">
                            <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400 mb-3">
                                Film summary
                            </div>
                            <div className="aspect-[2/3] overflow-hidden rounded-sm border border-white/10 mb-4 bg-ink-200">
                                <img src={film.poster_url} alt={film.title} className="w-full h-full object-cover" />
                            </div>
                            <h3 className="font-display text-2xl mb-1">{film.title}</h3>
                            <div className="text-silver-400 text-sm mb-2">
                                {film.country} · {film.language} · {film.runtime}m
                            </div>
                            <div className="text-platinum font-display text-xl">
                                ${Number(film.price_usd).toLocaleString()}
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}

function Row({ label, value, onChange, as = "input", type = "text", testid, required }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-widest text-silver-400 mb-1.5">
                {label}
            </label>
            {as === "textarea" ? (
                <textarea
                    rows={4}
                    value={value}
                    onChange={onChange}
                    required={required}
                    className="w-full bg-transparent border-b border-white/15 focus:border-platinum focus:outline-none py-2 text-white"
                    data-testid={testid}
                />
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={onChange}
                    required={required}
                    className="w-full bg-transparent border-b border-white/15 focus:border-platinum focus:outline-none py-2 text-white"
                    data-testid={testid}
                />
            )}
        </div>
    );
}

function Select({ label, value, options, onChange, testid }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-widest text-silver-400 mb-1.5">
                {label}
            </label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-ink-200 border border-white/10 focus:border-platinum focus:outline-none py-2 px-2 text-white text-sm rounded-sm"
                data-testid={testid}
            >
                {options.map((o) => (
                    <option key={o} value={o} className="bg-ink-200">
                        {o}
                    </option>
                ))}
            </select>
        </div>
    );
}
